import { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const ITEM_TYPES = [
  { id: 1, key: 'type1', ar: 'مبرد',    color: '#047857' },
  { id: 3, key: 'type3', ar: 'مجمد',    color: '#1d4ed8' },
  { id: 4, key: 'type4', ar: 'مقطعات',  color: '#b45309' },
  { id: 5, key: 'type5', ar: 'GB',      color: '#7c3aed' },
];

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function fmt(n) { return parseFloat(n || 0).toLocaleString('ar', { maximumFractionDigits: 0 }); }

export default function TeamPage() {
  const [period, setPeriod]   = useState(getCurrentPeriod());
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const toast = useToast();

  useEffect(() => { loadData(); }, [period]);

  async function loadData() {
    setLoading(true);
    try {
      const [users, commissions, allTargets] = await Promise.all([
        api.get('/users'),
        api.get(`/commission?period=${period}`),
        api.get(`/targets?period=${period}`),
      ]);

      const salesmen = users.filter(u =>
        ['salesman','supervisor','area_supervisor'].includes(u.role) && u.active
      );

      const commMap   = {};
      const targetMap = {};
      commissions.forEach(c => { commMap[c.user_id]   = c; });
      allTargets.forEach(t  => { targetMap[t.user_id] = t; });

      // Load actuals for each salesman
      const merged = await Promise.all(salesmen.map(async u => {
        let actual = null;
        try { actual = await api.get(`/netsuite/actuals/${u.id}/${period}`); } catch {}
        return {
          ...u,
          commission: commMap[u.id]   || null,
          target:     targetMap[u.id] || null,
          actual:     actual          || null,
        };
      }));

      setMembers(merged.sort((a,b) =>
        parseFloat(b.commission?.total_earned || 0) - parseFloat(a.commission?.total_earned || 0)
      ));
    } catch (err) {
      toast.error('خطأ في التحميل');
    } finally {
      setLoading(false);
    }
  }

  async function syncAll() {
    setSyncing(true);
    try {
      await api.post('/netsuite/sync/actuals', { period });
      // Auto-calculate all
      await api.post('/commission/calculate', { period }).catch(() => {});
      toast.success('تم تحديث بيانات الفريق');
      await loadData();
    } catch (err) { toast.error(err.message); }
    finally { setSyncing(false); }
  }

  const periods = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const totalCommission = members.reduce((s, m) => s + parseFloat(m.commission?.total_earned || 0), 0);

  return (
    <div className="page">
      <Header
        title="أداء الفريق"
        rightAction={
          <button style={styles.syncBtn} onClick={syncAll} disabled={syncing}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ animation: syncing ? 'spin 0.7s linear infinite' : 'none' }}>
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
            </svg>
          </button>
        }
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="page-content">

        {/* Period Selector */}
        <div style={styles.periodScroll}>
          {periods.map(p => (
            <button key={p} style={{
              ...styles.periodBtn,
              background: p === period ? 'var(--primary)' : 'white',
              color:      p === period ? 'white' : 'var(--gray-600)',
              border:     `1.5px solid ${p === period ? 'var(--primary)' : 'var(--gray-200)'}`,
            }} onClick={() => setPeriod(p)}>
              {MONTHS_AR[parseInt(p.split('-')[1]) - 1]} {p.split('-')[0]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-page"><div className="spinner" /><span>جاري التحميل...</span></div>
        ) : (
          <>
            {/* Summary */}
            <div style={styles.summaryCard}>
              <div style={styles.summaryItem}>
                <span style={styles.summaryNum}>{members.length}</span>
                <span style={styles.summaryLabel}>موظف</span>
              </div>
              <div style={styles.summaryDivider} />
              <div style={styles.summaryItem}>
                <span style={styles.summaryNum}>{totalCommission.toLocaleString('ar', { maximumFractionDigits: 0 })}</span>
                <span style={styles.summaryLabel}>إجمالي العمولات (ر.س)</span>
              </div>
            </div>

            {/* Member Cards */}
            {members.map(m => (
              <MemberCard
                key={m.id}
                member={m}
                expanded={expanded === m.id}
                onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function MemberCard({ member, expanded, onToggle }) {
  const { commission: comm, target, actual } = member;
  const total = parseFloat(comm?.total_earned || 0);

  return (
    <div style={styles.memberCard}>
      {/* Header row */}
      <div style={styles.memberHeader} onClick={onToggle}>
        <div style={styles.memberAvatar}>{member.name_ar?.charAt(0) || 'م'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gray-800)' }}>{member.name_ar}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{member.branch_name_ar || member.branch_name_en || '—'}</div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--primary)' }}>
            {total.toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س
          </div>
          <span style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 600,
            background: comm?.status === 'paid' ? 'var(--green-100)' : comm?.status === 'locked' ? '#dbeafe' : 'var(--gray-100)',
            color:      comm?.status === 'paid' ? 'var(--green-700)' : comm?.status === 'locked' ? '#1e40af' : 'var(--gray-500)',
          }}>
            {comm?.status === 'paid' ? 'مدفوع' : comm?.status === 'locked' ? 'مقفل' : 'جاري'}
          </span>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2"
             style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s', marginRight: 4, flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {/* Item type progress bars - always visible */}
      <div style={{ marginTop: 10 }}>
        {ITEM_TYPES.map(type => {
          const tval = parseFloat(target?.[`qty_${type.key}_target`] || 0);
          const aval = parseFloat(actual?.[`qty_${type.key}_actual`]  || 0);
          if (tval === 0 && aval === 0) return null;
          const pct = tval > 0 ? Math.min((aval / tval) * 100, 100) : 0;
          return (
            <div key={type.id} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: type.color }}>{type.ar}</span>
                <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                  {fmt(aval)} / {fmt(tval)} قطعة ({pct.toFixed(0)}%)
                </span>
              </div>
              <div className="kpi-bar" style={{ height: 6 }}>
                <div className={`kpi-bar-fill ${pct >= 90 ? 'green' : pct >= 60 ? 'yellow' : 'red'}`}
                     style={{ width: `${pct}%`, background: type.color + 'cc' }} />
              </div>
            </div>
          );
        }).filter(Boolean)}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray-100)' }}>
          <div style={styles.detailGrid}>
            {[
              { label: 'عمولة الكميات',  value: fmt(comm?.qty_earned        || 0) + ' ر.س' },
              { label: 'المرتجع الرديء', value: `${parseFloat(actual?.bad_return_actual_pct || 0).toFixed(1)}% → ${fmt(comm?.bad_return_earned || 0)} ر.س` },
              { label: 'التغطية',        value: `${actual?.coverage_actual || 0} عميل → ${fmt(comm?.coverage_earned || 0)} ر.س` },
              { label: 'التحصيل',        value: `${parseFloat(actual?.collection_actual_pct || 0).toFixed(1)}% → ${fmt(comm?.collection_earned || 0)} ر.س` },
              { label: 'الهدف الخاص',    value: `${fmt(comm?.specific_earned || 0)} ر.س` },
            ].map(item => (
              <div key={item.label} style={styles.detailRow}>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{item.label}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-800)' }}>{item.value}</span>
              </div>
            ))}
            {actual?.collection_refund_amount > 0 && (
              <div style={{ ...styles.detailRow, gridColumn: '1/-1', background: '#fff7ed', borderRadius: 6, padding: '4px 8px' }}>
                <span style={{ fontSize: 10, color: '#b45309' }}>مرتجعات مخصومة من التحصيل</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#b45309' }}>{fmt(actual.collection_refund_amount)} ر.س</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  periodScroll: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 4, scrollbarWidth: 'none' },
  periodBtn: { padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-main)', transition: 'all 0.2s' },
  syncBtn: { background: 'var(--gray-100)', border: 'none', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-600)' },
  summaryCard: { background: 'linear-gradient(135deg, var(--primary-dark), var(--primary))', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-around', boxShadow: 'var(--shadow-green)' },
  summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  summaryNum: { fontSize: 24, fontWeight: 800, color: 'white' },
  summaryLabel: { fontSize: 11, color: 'rgba(255,255,255,0.7)' },
  summaryDivider: { width: 1, height: 40, background: 'rgba(255,255,255,0.2)' },
  memberCard: { background: 'white', borderRadius: 14, padding: '14px 16px', marginBottom: 8, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' },
  memberHeader: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 4 },
  memberAvatar: { width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--green-400))', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 6 },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
};
